import {
  doc,
  getDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type DocumentData,
} from 'firebase/firestore'
import { MAX_BIO_LENGTH, type User, type UserSocials } from '@/types'
import { toDate } from '@/utils/timestamp'
import { normalizeHandle, isValidHandle, normalizeSocial } from '@/utils/validation'

/**
 * userService — user profiles and the globally-unique handle registry.
 *
 * Handle uniqueness is enforced by a parallel `handles/{handle}` collection that
 * maps a normalized handle to its owning uid. Claims run inside a Firestore
 * transaction (claimHandle / createUser) so two users racing for the same handle
 * cannot both win: the transaction re-reads the handle doc and aborts if it is
 * already owned by someone else, retrying automatically on contention.
 */

/** Thrown when a requested handle is already owned by another user. */
export class HandleTakenError extends Error {
  constructor(public readonly handle: string) {
    super(`Handle @${handle} is already taken`)
    this.name = 'HandleTakenError'
  }
}

/** Thrown when a handle fails the format rules. */
export class InvalidHandleError extends Error {
  constructor(public readonly handle: string) {
    super(`Handle @${handle} is invalid`)
    this.name = 'InvalidHandleError'
  }
}

const emptySocials: UserSocials = {
  instagram: null,
  tiktok: null,
  snapchat: null,
}

/** Map a raw `users/{id}` document to a typed User (Timestamp -> Date). */
const mapUser = (userId: string, data: DocumentData): User => ({
  userId,
  handle: data.handle,
  bio: data.bio ?? '',
  homeCity: data.homeCity ?? null,
  photoUrl: data.photoUrl ?? null,
  socials: {
    instagram: data.socials?.instagram ?? null,
    tiktok: data.socials?.tiktok ?? null,
    snapchat: data.socials?.snapchat ?? null,
  },
  blockedUserIds: data.blockedUserIds ?? [],
  createdAt: toDate(data.createdAt),
})

/** Persist a user's block list (the set of user ids they've blocked). */
export const setBlockedUsers = async (
  db: Firestore,
  userId: string,
  blockedUserIds: string[],
): Promise<void> => {
  await updateDoc(doc(db, 'users', userId), { blockedUserIds })
}

/** Fetch a user profile by uid, or null if no account exists for it. */
export const getUserById = async (
  db: Firestore,
  userId: string,
): Promise<User | null> => {
  const snap = await getDoc(doc(db, 'users', userId))
  if (!snap.exists()) return null
  return mapUser(snap.id, snap.data())
}

/**
 * Resolve a public profile by handle: read `handles/{handle}` → uid → the user
 * doc. Returns null for an unknown or malformed handle. Powers the public
 * profile page reached by tapping a handle.
 */
export const getUserByHandle = async (
  db: Firestore,
  rawHandle: string,
): Promise<User | null> => {
  const handle = normalizeHandle(rawHandle)
  if (!isValidHandle(handle)) return null
  const handleSnap = await getDoc(doc(db, 'handles', handle))
  if (!handleSnap.exists()) return null
  const uid = handleSnap.data().userId as string | undefined
  if (!uid) return null
  return getUserById(db, uid)
}

/**
 * Check whether a handle is free. Returns true when unclaimed, or when already
 * claimed by `forUserId` (so a user "keeping" their own handle reads available).
 * Invalid handles always read unavailable.
 */
export const isHandleAvailable = async (
  db: Firestore,
  rawHandle: string,
  forUserId?: string,
): Promise<boolean> => {
  const handle = normalizeHandle(rawHandle)
  if (!isValidHandle(handle)) return false
  const snap = await getDoc(doc(db, 'handles', handle))
  if (!snap.exists()) return true
  return forUserId !== undefined && snap.data().userId === forUserId
}

/**
 * Atomically claim/rename a handle for an existing (or about-to-exist) user.
 * Reserves `handles/{handle}`, writes `users/{userId}.handle`, and releases the
 * user's previous handle. Throws {@link HandleTakenError} on a lost race and
 * {@link InvalidHandleError} on a malformed handle.
 *
 * @returns the normalized handle that was claimed.
 */
export const claimHandle = async (
  db: Firestore,
  userId: string,
  rawHandle: string,
): Promise<string> => {
  const handle = normalizeHandle(rawHandle)
  if (!isValidHandle(handle)) throw new InvalidHandleError(handle)

  await runTransaction(db, async (tx) => {
    const handleRef = doc(db, 'handles', handle)
    const userRef = doc(db, 'users', userId)

    // All reads must precede writes within a transaction.
    const handleSnap = await tx.get(handleRef)
    if (handleSnap.exists() && handleSnap.data().userId !== userId) {
      throw new HandleTakenError(handle)
    }
    const userSnap = await tx.get(userRef)
    const previous = userSnap.exists()
      ? (userSnap.data().handle as string | undefined)
      : undefined

    tx.set(handleRef, { userId })
    // Stamp createdAt when this claim is also creating the profile (a first-time
    // sign-in that skipped the full AccountSetup flow), so the user doc isn't
    // left without it.
    tx.set(
      userRef,
      userSnap.exists() ? { handle } : { handle, createdAt: serverTimestamp() },
      { merge: true },
    )
    if (previous && previous !== handle) {
      tx.delete(doc(db, 'handles', previous))
    }
  })

  return handle
}

/** Fields supplied when creating a brand-new account. */
export interface CreateUserInput {
  userId: string
  handle: string
  bio?: string
  homeCity?: string | null
  photoUrl?: string | null
  socials?: Partial<UserSocials>
}

/**
 * Create a new user profile and reserve its handle in one transaction, so the
 * account never exists without a uniquely-held handle. Throws on a taken or
 * invalid handle.
 */
export const createUser = async (
  db: Firestore,
  input: CreateUserInput,
): Promise<void> => {
  const handle = normalizeHandle(input.handle)
  if (!isValidHandle(handle)) throw new InvalidHandleError(handle)

  await runTransaction(db, async (tx) => {
    const handleRef = doc(db, 'handles', handle)
    const handleSnap = await tx.get(handleRef)
    if (handleSnap.exists() && handleSnap.data().userId !== input.userId) {
      throw new HandleTakenError(handle)
    }
    tx.set(handleRef, { userId: input.userId })
    tx.set(doc(db, 'users', input.userId), {
      handle,
      bio: (input.bio ?? '').slice(0, MAX_BIO_LENGTH),
      homeCity: input.homeCity ?? null,
      photoUrl: input.photoUrl ?? null,
      socials: { ...emptySocials, ...(input.socials ?? {}) },
      createdAt: serverTimestamp(),
    })
  })
}

/** Profile fields a user may edit directly (handle is changed via claimHandle). */
export interface UpdateUserInput {
  bio?: string
  homeCity?: string | null
  photoUrl?: string | null
  socials?: Partial<UserSocials>
}

/**
 * Update editable profile fields. Social handles are normalized (strip `@`,
 * reduce pasted URLs to the username). Does not change the handle.
 */
export const updateUser = async (
  db: Firestore,
  userId: string,
  patch: UpdateUserInput,
): Promise<void> => {
  const update: DocumentData = {}
  if (patch.bio !== undefined) update.bio = patch.bio.slice(0, MAX_BIO_LENGTH)
  if (patch.homeCity !== undefined) update.homeCity = patch.homeCity?.trim() || null
  if (patch.photoUrl !== undefined) update.photoUrl = patch.photoUrl
  if (patch.socials) {
    if (patch.socials.instagram !== undefined)
      update['socials.instagram'] = normalizeSocial(patch.socials.instagram)
    if (patch.socials.tiktok !== undefined)
      update['socials.tiktok'] = normalizeSocial(patch.socials.tiktok)
    if (patch.socials.snapchat !== undefined)
      update['socials.snapchat'] = normalizeSocial(patch.socials.snapchat)
  }
  await updateDoc(doc(db, 'users', userId), update)
}
